import { useId, useState } from "react";
import { itemImagePresentation, type ImageReference } from "@um/contracts";

export interface ItemImageProps {
  image?: ImageReference;
  alt: string;
  variation?: string;
  className?: string;
  loading?: "eager" | "lazy";
  onLoad?: () => void;
  onError?: () => void;
}

/** A single item variation, cropped consistently in cards, slots and detail views. */
export function ItemImage({
  image,
  alt,
  variation,
  className,
  loading = "lazy",
  onLoad,
  onError,
}: ItemImageProps) {
  const clipId = useId();
  const { url, panel } = itemImagePresentation(image, variation);
  if (!url)
    return (
      <span
        className={className}
        role={alt ? "img" : undefined}
        aria-label={alt ? `${alt} — artwork unavailable` : undefined}
      />
    );
  if (!panel)
    return (
      <img
        src={url}
        alt={alt}
        draggable={false}
        loading={loading}
        onLoad={onLoad}
        onError={onError}
        className={className}
        style={{ objectFit: "contain" }}
      />
    );
  const f = panel.frame;
  return (
    <svg
      className={className}
      viewBox={`${f.left} ${f.top} ${f.width} ${f.height}`}
      preserveAspectRatio="xMidYMid meet"
      overflow="hidden"
      role={alt ? "img" : undefined}
      aria-label={alt || undefined}
      aria-hidden={!alt || undefined}
      data-item-variation={panel.name}
    >
      <defs>
        <clipPath id={clipId}>
          <rect x={f.left} y={f.top} width={f.width} height={f.height} />
        </clipPath>
      </defs>
      <image
        clipPath={`url(#${clipId})`}
        href={url}
        width={panel.width}
        height={panel.height}
        onLoad={onLoad}
        onError={onError}
      />
    </svg>
  );
}

/** Explicit variation choices never change the stored item or its default panel. */
export function ItemArtwork({
  image,
  alt,
  className = "h-48 w-full",
}: ItemImageProps) {
  const [choice, setChoice] = useState<{
    image: ImageReference;
    name: string;
  }>();
  const { panel, panels } = itemImagePresentation(
    image,
    choice && choice.image === image ? choice.name : undefined
  );
  return (
    <div className="space-y-3">
      <ItemImage
        image={image}
        alt={alt}
        variation={panel?.name}
        className={className}
      />
      {panels.length > 1 && (
        <div
          role="group"
          aria-label={`${alt} variations`}
          className="flex flex-wrap justify-center gap-2"
        >
          {panels.map((p) => (
            <button
              type="button"
              key={p.name}
              title={p.description || p.name}
              aria-label={`Show ${p.name}`}
              aria-pressed={panel?.name === p.name}
              className={`rounded-md border p-1 ${panel?.name === p.name ? "ring-2 ring-teal-600" : ""}`}
              onClick={() => image && setChoice({ image, name: p.name })}
            >
              <ItemImage
                image={image}
                alt=""
                variation={p.name}
                className="h-12 w-12"
              />
              <span className="block max-w-24 truncate text-xs">{p.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
